import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import {
    Avatar,
    Box,
    IconButton,
    Paper,
    Stack,
    Typography,
    Menu,
    MenuItem,
    Button,
    Fade
} from "@mui/material";
import { notificationStyles } from "../../pages/chat/MuiChatThread";
import { ChatMessage, getCurrentOrchestration } from "../../common/models";
import { useAppStateContext } from "../../state/AppContextProvider";
import { useContext } from "react";
import { FooterButtonContext } from "../../pages/layout/MuiLayout";

import ReactMarkdown from 'react-markdown'
import { Prism, SyntaxHighlighterProps } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism'
import DOMPurify from 'dompurify'
import remarkGfm from 'remark-gfm'
import supersub from 'remark-supersub'

import { XSSAllowTags } from '../../constants/xssAllowTags'
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { copyToClipBoard } from "../../utils/copyContent";
import AgentIcon from "../../assets/FightLessWinMore_Cover-150px.png";
import AnswerFooterButtons from './MuiAnswerFooter';
import GoldParticleContainer from "../common/Generator"; // adjust path if needed
import LoadingSpinner from "../common/LoadingSpinner"; // Add this import

const SyntaxHighlighter = (Prism as any) as React.FC<SyntaxHighlighterProps>;

interface Props {
    message: ChatMessage;
    generating: boolean;
    showExportButton: boolean;
    onExportClick?: () => void; // Made optional since we're using our own handler
    showFooterButtons?: boolean; // <-- Add this line
    onFooterButtonClick?: (input: string | null) => void; // <-- Add this line
    children?: React.ReactNode; // <-- Add this line
}
const exportOptions = [
    { label: "Export Conversation", value: "ExportHistory" },
    { label: "POV", value: "pov" },
    { label: "Simulation", value: "simulation" }
];

const MuiAiAnswer = ({ message, generating, showExportButton, onExportClick, showFooterButtons, onFooterButtonClick, children }: Props) => {
    const { setFooterButtonValue } = useContext(FooterButtonContext);
    const [copied, setCopied] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { exportChat, state } = useAppStateContext();
    const [footerInput, setFooterInput] = useState<string | null>(null);


    useEffect(() => {
        if (copied) {
            setTimeout(() => {
                setCopied(false);
            }, 3000);
        }
    }, [copied]);

    const components = {
        code({ node, ...props }: { node?: any;[key: string]: any }) {
            let language
            if (props.className) {
                const match = props.className.match(/language-(\w+)/)
                language = match ? match[1] : undefined
            }
            const codeString = node.children[0].value ?? ''
            return (
                <SyntaxHighlighter style={nord} language={language} PreTag="div" {...props}>
                    {codeString}
                </SyntaxHighlighter>
            )
        }
    }
    const handleExport = async (exportType: string) => {
        try {
            const sessionId = state.currentConversation.sessionId;
            if (!sessionId) {
                const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
                DownloadNotificationService.error(
                    'Export Error',
                    'No active session found. Please start a conversation first.'
                );
                return;
            }
            const abortController = new AbortController();
            await exportChat(exportType, sessionId, abortController.signal);
            // No need to alert or handle downloadUrl, the file will download automatically
        } catch (err) {
            // Import the notification service for better error handling
            const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
            DownloadNotificationService.error(
                'Export Failed',
                'There was an error exporting the chat content. Please try again.'
            );
            console.error(err);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleExportConversation = async () => {
        setAnchorEl(null);
        try {
            const sessionId = state.currentConversation.sessionId;
            if (!sessionId) {
                const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
                DownloadNotificationService.error(
                    'Export Error',
                    'No active session found. Please start a conversation first.'
                );
                return;
            }
            const abortController = new AbortController();
            await exportChat("chathistory", sessionId, abortController.signal); // <-- use "chathistory"
        } catch (err) {
            // Import the notification service for better error handling
            const { DownloadNotificationService } = await import('../../services/downloadNotificationService');
            DownloadNotificationService.error(
                'Export Failed',
                'There was an error exporting the conversation history. Please try again.'
            );
            console.error(err);
        }
    };

    const orchState = state.currentOrchState;

    const currentOrch = getCurrentOrchestration(state.currentOrchState);

    // Determine export type based on current agent
    const getExportType = () => {
        const currentAgent = currentOrch?.currentAgent?.toLowerCase();
        if (currentAgent?.includes('pov')) return 'pov';
        if (currentAgent?.includes('simulation')) return 'simulation';
        if (currentAgent?.includes('negotiation')) return 'history';
        return 'history'; // Default to conversation history
    };

    const handleExportToPDF = async () => {
        const exportType = getExportType();
        await handleExport(exportType);
    };

    // Split recommendations and prompts into arrays
    const handoffButtons = currentOrch?.handoffRecommendation?.split('|').map(q => q.trim()).filter(Boolean) || [];
    const nextPromptButtons = orchState?.nextPrompt?.split('|').map(q => q.trim()).filter(Boolean) || [];
    const buttonLabels = [...handoffButtons, ...nextPromptButtons].slice(0, 4);

    return (
        <Box sx={{ width: "100%" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} width="100%" alignItems={{ xs: "flex-start", sm: "flex-start" }}>
                <Box
                    component="img"
                    src={AgentIcon}
                    alt="AI"
                    sx={{ width: 50, height: 50, objectFit: "contain" }}
                />
                <Paper
                    elevation={1}
                    sx={{
                        flex: 1,
                        borderRadius: { xs: "0px 12px 12px 12px", sm: "0px 12px 12px 12px" },
                        border: generating ? "none" : "rgba(0, 0, 0, 1) 2px solid", // <-- conditional border
                        p: 2,
                        pb: 1,
                        boxShadow: "0px 3px 4px rgba(0, 0, 0, 0.04)",
                        bgcolor: notificationStyles[message.authorRole.label]?.bg || "grey.50",
                        color: notificationStyles[message.authorRole.label]?.color || "text.primary",
                        position: "relative",
                        maxWidth: { xs: "100%", sm: "80%" },
                        minHeight: "100px",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    <Fade in={generating} timeout={200} unmountOnExit>
                        <Box sx={{
                            position: "absolute",
                            width: "70%",
                            top: "0%",
                            left: "10%",
                            zIndex: 2,
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <LoadingSpinner text="Thinking" size={100} />
                        </Box>
                    </Fade>
                    {/* AI response rendering */}
                    {!generating ? (
                        children ? children : (
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm, supersub]}
                                children={DOMPurify.sanitize(message.content, { ALLOWED_TAGS: XSSAllowTags })}
                                components={{
                                    ...components,
                                    p: ({ node, ...props }) => (
                                        <Typography
                                            component="div"
                                            variant="body2"
                                            sx={{
                                                fontSize: {
                                                    xs: "14px",
                                                    sm: "15px",
                                                },
                                                lineHeight: {
                                                    xs: "1.2",
                                                    sm: "1.5",
                                                },
                                                width: "100%",
                                                wordBreak: "break-word",
                                            }}
                                            {...props as any}
                                        />
                                    ),
                                }}
                            />
                        )
                    ) : (
                        <Typography component="div" variant="body2">
                            {/* Generating answer... */}
                        </Typography>
                    )}

                    {showExportButton && (
                        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                variant="contained"
                                className="pdf_export"
                                onClick={handleExportToPDF}
                                sx={{
                                    padding: "8px 20px",
                                    borderRadius: "20px",
                                    fontWeight: 600,
                                    fontSize: 14,
                                    background: "linear-gradient(90deg, #e50c20 0%, #b8091a 100%)",
                                    color: "#fff",
                                    textTransform: "none",
                                    boxShadow: "0 2px 8px rgba(229, 12, 32, 0.3)",
                                    "&:hover": {
                                        background: "linear-gradient(90deg, #b8091a 0%, #e50c20 100%)",
                                        transform: "translateY(-1px)",
                                    },
                                    "&:active": {
                                        transform: "translateY(0px)",
                                    },
                                }}
                            >
                                Export to PDF
                            </Button>
                        </Box>
                    )}

                    {/* Action icons at the bottom right */}
                    {!generating && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
                            <IconButton
                                size="small"
                                onClick={async () => {
                                    await copyToClipBoard(message.content);
                                    setCopied(true);
                                }}
                                title={copied ? "Copied!" : "Copy"}
                                sx={{ color: 'custom.lockedPrimary' }}
                            >
                                <ContentCopyOutlinedIcon fontSize="small" color={copied ? "success" : "inherit"} />
                            </IconButton>
                            <IconButton size="small" title="Thumbs Up" sx={{ color: 'custom.lockedPrimary' }}>
                                <ThumbUpOutlinedIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Thumbs Down" sx={{ color: 'custom.lockedPrimary' }}>
                                <ThumbDownOutlinedIcon fontSize="small" />
                            </IconButton>
                            {/* Ellipsis IconButton now inside the action row */}
                            <IconButton
                                size="small"
                                sx={{ padding: "1px", borderRadius: "30px", color: 'custom.lockedPrimary' }}
                                onClick={handleMenuOpen}
                            >
                                <MoreVertIcon sx={{ width: 16, height: 16 }} />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                MenuListProps={{
                                    onMouseLeave: handleMenuClose,
                                    autoFocus: false,
                                }}
                            >
                                <MenuItem onClick={handleExportConversation}>
                                    Export Conversation
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}
                </Paper>
            </Stack>
            {showFooterButtons && !generating && !state.isReadOnlyMode && (
                <Box sx={{
                    ml: { xs: 0, sm: 7 }, // Match the left margin of the Paper (Avatar width + spacing)
                    maxWidth: { xs: "100%", sm: "80%" }, // Match the Paper's maxWidth
                    width: "100%"
                }}>
                    <AnswerFooterButtons onButtonClick={setFooterButtonValue} />
                </Box>
            )}
        </Box>
    );
};

export default MuiAiAnswer;
